import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    NumberField,
    EditButton,
    TextInput,
    SelectInput,
    FunctionField,
} from 'react-admin';
import { Chip } from '@mui/material';

/**
 * The school registry: every WiZi customer, one row each.
 *
 * Only role 1 reaches this screen -- the API refuses everyone else with a 403,
 * and Menu.tsx does not show it to them. That is worth stating because this is
 * the one list in the product that crosses schools: a school administrator
 * seeing it would be reading their competitors' contact details.
 *
 * TWO STATUS COLUMNS, DELIBERATELY
 * --------------------------------
 * `status` is the commercial state of the school -- onboarding, active,
 * suspended. `provision_state` is whether its database exists and is serving.
 * They are genuinely different facts and they disagree in the state that
 * matters most: a school created in this screen is 'onboarding' with no
 * database at all, and someone has to notice and press Provision.
 *
 * Collapsing them into one badge would hide exactly that case.
 */

const statusColour = (status: string): any => {
    switch (status) {
        case 'active':      return 'success';
        case 'onboarding':  return 'info';
        case 'suspended':   return 'warning';
        case 'offboarding': return 'error';
        case 'archived':    return 'default';
        default:            return 'default';
    }
};

const provisionColour = (state: string): any => {
    switch (state) {
        case 'active':          return 'success';
        case 'not provisioned': return 'warning';
        case 'provisioning':    return 'info';
        case 'migrating':       return 'info';
        case 'readonly':        return 'warning';
        default:                return 'error';
    }
};

const filters = [
    <TextInput source="q" label="Search" alwaysOn resettable />,
    <SelectInput
        source="status"
        label="Status"
        choices={[
            { id: 'onboarding',  name: 'Onboarding' },
            { id: 'active',      name: 'Active' },
            { id: 'suspended',   name: 'Suspended' },
            { id: 'offboarding', name: 'Off-boarding' },
            { id: 'archived',    name: 'Archived' },
        ]}
    />,
    <TextInput source="city" label="City" />,
    <TextInput source="board" label="Board" />,
];

export const SchoolList = props => (
    <List
        title="Schools"
        {...props}
        filters={filters}
        sort={{ field: 'name', order: 'ASC' }}
        // No bulk actions. Deleting a school is not a checkbox: it drops a
        // database, deletes a storage prefix and rewrites member accounts.
        // api/bin/offboard_school.php does that, with a receipt and a typed
        // confirmation, and it should stay the only way.
        exporter={false}
    >
        <Datagrid rowClick="edit" bulkActionButtons={false}>
            <EditButton />
            <TextField source="code" label="Code" />
            <TextField source="name" label="School" />
            <TextField source="city" label="City" />
            <TextField source="board" label="Board" />

            <FunctionField
                label="Status"
                sortBy="status"
                render={(r: any) => (
                    <Chip size="small" label={r.status} color={statusColour(r.status)} />
                )}
            />

            <FunctionField
                label="Database"
                sortBy="db_status"
                render={(r: any) => (
                    <Chip
                        size="small"
                        variant={r.provisioned ? 'filled' : 'outlined'}
                        label={r.provision_state}
                        color={provisionColour(r.provision_state)}
                    />
                )}
            />

            <TextField source="db_name" label="DB name" />
            <NumberField source="schema_version" label="Schema" />
            <DateField source="last_migrated_at" label="Last migrated" showTime />
        </Datagrid>
    </List>
);
